import { authedFetch } from "./restClient.js";
import { grpcStatusName } from "./grpcStatus.js";
import { tenantViewSecretOverrideDigest } from "./tenantViewSecretOverrides.js";
import { getAuthIdentityEpoch, onAuthIdentityChange } from "./authTokenStore.js";

const TEXT_ENCODER = new TextEncoder();
const MAX_PAGE_SIZE = 200;
const PORTABLE_UUID_V4 =
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const KIND_TO_WIRE = Object.freeze({
    kernel: "TENANT_VIEW_KIND_KERNEL",
    canvas: "TENANT_VIEW_KIND_CANVAS",
});

const VISIBILITY_TO_WIRE = Object.freeze({
    draft: "TENANT_VIEW_VISIBILITY_DRAFT",
    published: "TENANT_VIEW_VISIBILITY_PUBLISHED",
});

const RUN_OUTCOME_TO_WIRE = Object.freeze({
    success: "TENANT_VIEW_RUN_OUTCOME_SUCCESS",
    empty: "TENANT_VIEW_RUN_OUTCOME_EMPTY",
    unavailable: "TENANT_VIEW_RUN_OUTCOME_UNAVAILABLE",
    refused: "TENANT_VIEW_RUN_OUTCOME_REFUSED",
    failed: "TENANT_VIEW_RUN_OUTCOME_FAILED",
    cancelled: "TENANT_VIEW_RUN_OUTCOME_CANCELLED",
});

const RUN_REASON_CATEGORY_TO_WIRE = Object.freeze({
    none: "TENANT_VIEW_RUN_REASON_CATEGORY_NONE",
    infrastructure: "TENANT_VIEW_RUN_REASON_CATEGORY_INFRASTRUCTURE",
    scheduler: "TENANT_VIEW_RUN_REASON_CATEGORY_SCHEDULER",
    content: "TENANT_VIEW_RUN_REASON_CATEGORY_CONTENT",
});

const DEFAULT_RUN_REASON_CATEGORY = Object.freeze({
    success: "none",
    empty: "content",
    unavailable: "infrastructure",
    refused: "content",
    failed: "content",
    cancelled: "scheduler",
});

const KERNEL_DEFINITION_KEYS = new Set([
    "id",
    "kind",
    "command",
    "title",
    "description",
    "scopeMode",
    "agentic",
    "params",
    "steps",
    "render",
    "renderSource",
]);
const CANVAS_DEFINITION_KEYS = new Set([
    "id",
    "kind",
    "slug",
    "title",
    "description",
    "items",
    "rows",
]);
const STEP_KEYS = Object.freeze({
    analytics_query: new Set(["id", "kind", "inputs", "spec"]),
    python: new Set(["id", "kind", "inputs", "code", "packages", "entry"]),
    mcp_tool: new Set([
        "id",
        "kind",
        "inputs",
        "server",
        "tool",
        "args",
        "argsFrom",
    ]),
    agent: new Set(["id", "kind", "inputs", "prompt", "outputSchema", "role"]),
});
const PARAMETER_KEYS = new Set(["type", "default"]);
const CANVAS_ROW_KEYS = new Set(["cells"]);
const CANVAS_CELL_KEYS = new Set([
    "kernel_id",
    "command",
    "caption",
    "parameters",
    "width",
]);

export const DEFAULT_TENANT_VIEW_CAPABILITIES = Object.freeze({
    currentFormatVersion: 1,
    minWritableFormatVersion: 0,
    knownStepKinds: Object.freeze(["agent", "analytics_query", "mcp_tool", "python"]),
    publishableStepKinds: Object.freeze(["agent", "analytics_query", "python"]),
    maxKernelSteps: 20,
    maxCanvasRows: 20,
    maxCanvasCells: 20,
    maxDefinitionBytes: 262144,
    maxDefinitionDepth: 32,
    maxPortableArtifactBytes: 6 * 1024 * 1024,
    secretDetectorVersion: "",
    maxSecretFindingOverrides: 0,
});

export class TenantViewApiError extends Error {
    constructor(message, {
        status = null,
        code = "",
        serverMessage = "",
        details = [],
        cause,
    } = {}) {
        super(message);
        this.name = "TenantViewApiError";
        this.status = status;
        this.code = code;
        this.serverMessage = serverMessage;
        this.details = details;
        if (cause !== undefined) this.cause = cause;
    }
}

export class TenantViewConflictError extends TenantViewApiError {
    constructor(message, options = {}) {
        super(message, options);
        this.name = "TenantViewConflictError";
    }
}

export class TenantViewUnavailableError extends TenantViewApiError {
    constructor(message, options = {}) {
        super(message, options);
        this.name = "TenantViewUnavailableError";
    }
}

function pick(value, snake, camel) {
    if (!value || typeof value !== "object") return undefined;
    return value[camel] ?? value[snake];
}

function own(value, key) {
    return Object.prototype.hasOwnProperty.call(value, key);
}

function validationError(message) {
    return new TenantViewApiError(message, { code: "CLIENT_VALIDATION" });
}

function boundedUtf8String(value, label, maximum, { nonblank = false } = {}) {
    const text = String(value ?? "");
    if (nonblank && !text.trim()) throw validationError(`${label} must not be blank`);
    const bytes = TEXT_ENCODER.encode(text).byteLength;
    if (bytes > maximum) {
        throw validationError(
            `${label} is ${bytes} UTF-8 bytes; maximum is ${maximum}`,
        );
    }
    return text;
}

function responseError(message) {
    return new TenantViewApiError(message, { code: "INVALID_RESPONSE" });
}

function finiteInteger(value, fallback, { min = 0 } = {}) {
    if (value === undefined || value === null || value === "") return fallback;
    const number = Number(value);
    return Number.isSafeInteger(number) && number >= min ? number : fallback;
}

function stringArray(value, fallback) {
    if (value === undefined) return [...fallback];
    if (!Array.isArray(value)) throw responseError("Tenant view capability kinds must be arrays");
    return [...new Set(value.filter((item) => typeof item === "string" && item.length > 0))];
}

export function normalizeTenantViewCapabilities(input) {
    const nested = pick(input, "tenant_view_capabilities", "tenantViewCapabilities");
    const raw = nested ?? input;
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
        throw responseError("Tenant view capabilities are missing");
    }

    const currentFormatVersion = finiteInteger(
        pick(raw, "current_format_version", "currentFormatVersion"),
        DEFAULT_TENANT_VIEW_CAPABILITIES.currentFormatVersion,
    );
    const minWritableFormatVersion = finiteInteger(
        pick(raw, "min_writable_format_version", "minWritableFormatVersion"),
        DEFAULT_TENANT_VIEW_CAPABILITIES.minWritableFormatVersion,
    );
    if (minWritableFormatVersion > currentFormatVersion) {
        throw responseError("Tenant view writable format range is invalid");
    }

    const knownStepKinds = stringArray(
        pick(raw, "known_step_kinds", "knownStepKinds"),
        [],
    );
    const publishableStepKinds = stringArray(
        pick(raw, "publishable_step_kinds", "publishableStepKinds"),
        [],
    );
    const known = new Set(knownStepKinds);
    if (publishableStepKinds.some((kind) => !known.has(kind))) {
        throw responseError("Tenant view publishable step kinds must be known step kinds");
    }

    return {
        currentFormatVersion,
        minWritableFormatVersion,
        knownStepKinds,
        publishableStepKinds,
        maxKernelSteps: finiteInteger(
            pick(raw, "max_kernel_steps", "maxKernelSteps"),
            DEFAULT_TENANT_VIEW_CAPABILITIES.maxKernelSteps,
            { min: 1 },
        ),
        maxCanvasRows: finiteInteger(
            pick(raw, "max_canvas_rows", "maxCanvasRows"),
            DEFAULT_TENANT_VIEW_CAPABILITIES.maxCanvasRows,
            { min: 1 },
        ),
        maxCanvasCells: finiteInteger(
            pick(raw, "max_canvas_cells", "maxCanvasCells"),
            DEFAULT_TENANT_VIEW_CAPABILITIES.maxCanvasCells,
            { min: 1 },
        ),
        maxDefinitionBytes: finiteInteger(
            pick(raw, "max_definition_bytes", "maxDefinitionBytes"),
            DEFAULT_TENANT_VIEW_CAPABILITIES.maxDefinitionBytes,
            { min: 1 },
        ),
        maxDefinitionDepth: finiteInteger(
            pick(raw, "max_definition_depth", "maxDefinitionDepth"),
            DEFAULT_TENANT_VIEW_CAPABILITIES.maxDefinitionDepth,
            { min: 1 },
        ),
        maxPortableArtifactBytes: finiteInteger(
            pick(raw, "max_portable_artifact_bytes", "maxPortableArtifactBytes"),
            DEFAULT_TENANT_VIEW_CAPABILITIES.maxPortableArtifactBytes,
            { min: 1 },
        ),
        secretDetectorVersion: String(
            pick(raw, "secret_detector_version", "secretDetectorVersion")
            ?? DEFAULT_TENANT_VIEW_CAPABILITIES.secretDetectorVersion,
        ),
        maxSecretFindingOverrides: finiteInteger(
            pick(raw, "max_secret_finding_overrides", "maxSecretFindingOverrides"),
            DEFAULT_TENANT_VIEW_CAPABILITIES.maxSecretFindingOverrides,
        ),
    };
}

function normalizeEnum(value, prefix) {
    if (typeof value !== "string") return "unspecified";
    return value.startsWith(prefix)
        ? value.slice(prefix.length).toLowerCase()
        : value.toLowerCase();
}

function wireEnum(value, map, prefix, label, { allowUnspecified = false } = {}) {
    if (value === undefined || value === null || value === "") {
        if (allowUnspecified) return undefined;
        throw validationError(`${label} is required`);
    }
    if (typeof value === "string" && value.startsWith(prefix)) return value;
    const normalized = String(value).toLowerCase();
    const wire = map[normalized];
    if (!wire) throw validationError(`${label} is not recognized`);
    return wire;
}

function requireId(value, label = "tenantViewId") {
    if (typeof value !== "string" || value.length === 0) {
        throw validationError(`${label} must be a non-empty base64 string`);
    }
    return value;
}

function createRequestId(value) {
    let decoded;
    try {
        decoded = typeof value === "string" ? atob(value) : null;
    } catch {
        decoded = null;
    }
    if (decoded === null || btoa(decoded) !== value) {
        throw validationError("createRequestId must be canonical base64");
    }
    if (decoded.length !== 16) {
        throw validationError("createRequestId must encode one stable 16-byte UUID");
    }
    return value;
}

function canonicalBase64(value, label, expectedBytes = null) {
    let decoded;
    try {
        decoded = typeof value === "string" ? atob(value) : null;
    } catch {
        decoded = null;
    }
    if (
        decoded === null
        || btoa(decoded) !== value
        || (expectedBytes !== null && decoded.length !== expectedBytes)
    ) {
        throw validationError(
            `${label} must be canonical base64${expectedBytes === null
                ? ""
                : ` encoding ${expectedBytes} bytes`}`,
        );
    }
    return value;
}

function secretFindingOverrideBody(input, {
    detectorVersion,
    contentDigest,
    objectId = "",
} = {}) {
    const kind = boundedUtf8String(input?.kind, "secret override kind", 64, {
        nonblank: true,
    });
    const jsonPointer = boundedUtf8String(
        input?.jsonPointer ?? input?.json_pointer,
        "secret override JSON pointer",
        1024,
    );
    const justification = boundedUtf8String(
        input?.justification,
        "secret override justification",
        500,
        { nonblank: true },
    ).trim();
    if (TEXT_ENCODER.encode(justification).byteLength < 8) {
        throw validationError(
            "secret override justification must contain at least 8 UTF-8 bytes",
        );
    }
    return {
        objectId,
        kind,
        jsonPointer,
        contentDigest: canonicalBase64(
            input?.contentDigest ?? contentDigest,
            "secret override contentDigest",
            32,
        ),
        detectorVersion: boundedUtf8String(
            input?.detectorVersion ?? detectorVersion,
            "secret override detectorVersion",
            64,
            { nonblank: true },
        ),
        justification,
    };
}

const PARAMETER_TYPE_TO_WIRE = Object.freeze({
    number: "TENANT_VIEW_PARAMETER_TYPE_NUMBER",
    string: "TENANT_VIEW_PARAMETER_TYPE_STRING",
    boolean: "TENANT_VIEW_PARAMETER_TYPE_BOOLEAN",
});

const PYTHON_PACKAGE_TO_WIRE = Object.freeze({
    numpy: "TENANT_VIEW_PYTHON_PACKAGE_NUMPY",
    pandas: "TENANT_VIEW_PYTHON_PACKAGE_PANDAS",
    scipy: "TENANT_VIEW_PYTHON_PACKAGE_SCIPY",
});
const PYTHON_PACKAGE_FROM_WIRE = Object.freeze(
    Object.fromEntries(
        Object.entries(PYTHON_PACKAGE_TO_WIRE).map(([name, wire]) => [wire, name]),
    ),
);

function typedInputs(inputs) {
    return Object.entries(inputs || {})
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([name, sourceStepId]) => ({ name, sourceStepId }));
}

// The concrete protobuf query cannot carry a numeric parameter placeholder.
// Defaults are only a wire skeleton; parameterTemplates restores the original
// expressions before storage and before any subsequent execution.
function typedAnalyticsQuery(spec, params) {
    const parameterTemplates = {};
    const lookup = (path) => {
        const [name, ...keys] = path.split(".");
        let value = params?.[name]?.default;
        for (const key of keys) value = value?.[key];
        return value;
    };
    const walk = (value, path) => {
        if (typeof value === "string" && /\{\{\s*[\w.]+\s*\}\}/.test(value)) {
            parameterTemplates[path] = value;
            const whole = /^\s*\{\{\s*([\w.]+)\s*\}\}\s*$/.exec(value);
            if (whole) return lookup(whole[1]) ?? null;
            return value.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_, key) => {
                const resolved = lookup(key);
                return resolved == null ? "" : typeof resolved === "string" ? resolved : JSON.stringify(resolved);
            });
        }
        if (Array.isArray(value)) return value.map((item, i) => walk(item, `${path}/${i}`));
        if (value && typeof value === "object") {
            return Object.fromEntries(Object.entries(value).map(([key, item]) => [
                key, walk(item, `${path}/${key.replaceAll("~", "~0").replaceAll("/", "~1")}`),
            ]));
        }
        return value;
    };
    const concrete = walk(spec, "");
    return Object.keys(parameterTemplates).length ? { templateSpec: concrete, parameterTemplates } : { spec: concrete };
}

function analyticsQueryFromTyped(query) {
    const spec = structuredClone(query.templateSpec || query.spec || {});
    for (const [pointer, template] of Object.entries(query.parameterTemplates || {})) {
        const keys = pointer.slice(1).split("/").map((key) => key.replaceAll("~1", "/").replaceAll("~0", "~"));
        let target = spec;
        for (const key of keys.slice(0, -1)) {
            if (!Object.prototype.hasOwnProperty.call(target, key) || target[key] == null) {
                Object.defineProperty(target, key, { value: {}, enumerable: true, writable: true, configurable: true });
            }
            target = target[key];
        }
        Object.defineProperty(target, keys.at(-1), { value: template, enumerable: true, writable: true, configurable: true });
    }
    return spec;
}

function typedDefinition(definition, kind, formatVersion) {
    if (!definition || typeof definition !== "object" || Array.isArray(definition)) {
        throw validationError("definition must be an object");
    }
    if (kind === "kernel") {
        const parameters = Object.entries(definition.params || {})
            .sort(([left], [right]) => left.localeCompare(right))
            .map(([name, parameter]) => {
                const type = PARAMETER_TYPE_TO_WIRE[parameter?.type];
                if (!type) throw validationError(`parameter ${name} has an unsupported type`);
                return {
                    name,
                    type,
                    ...(Object.prototype.hasOwnProperty.call(parameter, "default")
                        ? { defaultValue: parameter.default }
                        : {}),
                };
            });
        const steps = (definition.steps || []).map((step) => {
            const common = { id: String(step.id || ""), inputs: typedInputs(step.inputs) };
            if (step.kind === "analytics_query") {
                return { ...common, analyticsQuery: typedAnalyticsQuery(step.spec || {}, definition.params) };
            }
            if (step.kind === "python") {
                const packages = [...new Set(step.packages || [])].sort().map((name) => {
                    const value = PYTHON_PACKAGE_TO_WIRE[name];
                    if (!value) throw validationError(`Python package ${name} is unsupported`);
                    return value;
                });
                return {
                    ...common,
                    python: {
                        code: String(step.code || ""),
                        packages,
                        ...(step.entry == null ? {} : { entry: String(step.entry) }),
                    },
                };
            }
            if (step.kind === "mcp_tool") {
                return {
                    ...common,
                    mcpTool: {
                        server: String(step.server || ""),
                        tool: String(step.tool || ""),
                        args: step.args || {},
                        ...(step.argsFrom == null
                            ? {}
                            : { argsFromStepId: String(step.argsFrom) }),
                    },
                };
            }
            if (step.kind === "agent") {
                return {
                    ...common,
                    agent: {
                        prompt: String(step.prompt || ""),
                        outputSchema: step.outputSchema || {},
                        role: String(step.role || ""),
                    },
                };
            }
            throw validationError(`kernel step kind ${step.kind} is unsupported`);
        });
        return {
            kernel: {
                command: String(definition.command || ""),
                ...(definition.description == null
                    ? {}
                    : { description: String(definition.description) }),
                scopeMode: definition.scopeMode === "scoped"
                    ? "TENANT_VIEW_SCOPE_MODE_SCOPED"
                    : "TENANT_VIEW_SCOPE_MODE_GLOBAL",
                agentic: definition.agentic === true,
                parameters,
                steps,
                renderConfigVersion: 1,
                render: definition.render || {},
                ...(definition.renderSource == null
                    ? {}
                    : { renderSourceStepId: String(definition.renderSource) }),
            },
        };
    }
    const rows = formatVersion === 0
        ? [{ cells: definition.items || [] }]
        : (definition.rows || []);
    return {
        canvas: {
            slug: String(definition.slug || ""),
            ...(definition.description == null
                ? {}
                : { description: String(definition.description) }),
            rows: rows.map((row) => ({
                cells: (row.cells || []).map((cell) => ({
                    kernelId: requireId(cell.kernel_id, "kernelId"),
                    ...(cell.command == null ? {} : { command: String(cell.command) }),
                    ...(cell.caption == null ? {} : { caption: String(cell.caption) }),
                    ...(cell.parameters == null ? {} : { parameters: cell.parameters }),
                    ...(own(cell, "width") ? { width: cell.width } : {}),
                })),
            })),
        },
    };
}

function canonicalDefinitionFromTyped(
    definition,
    kind,
    name,
    title,
    destinationId,
    formatVersion,
) {
    if (kind === "kernel") {
        const kernel = definition.kernel;
        const params = Object.fromEntries(
            (kernel.parameters || []).map((parameter) => {
                const type = normalizeEnum(parameter.type, "TENANT_VIEW_PARAMETER_TYPE_");
                return [
                    parameter.name,
                    {
                        type,
                        ...(own(parameter, "defaultValue")
                            ? { default: parameter.defaultValue }
                            : {}),
                    },
                ];
            }),
        );
        const steps = (kernel.steps || []).map((step) => {
            const value = { id: step.id };
            if (step.inputs?.length) {
                value.inputs = Object.fromEntries(
                    step.inputs.map((input) => [input.name, input.sourceStepId]),
                );
            }
            if (step.analyticsQuery) {
                Object.assign(value, {
                    kind: "analytics_query",
                    spec: analyticsQueryFromTyped(step.analyticsQuery),
                });
            } else if (step.python) {
                const packages = [...new Set(
                    (step.python.packages || [])
                        .map((wire) => PYTHON_PACKAGE_FROM_WIRE[wire])
                        .filter(Boolean),
                )].sort();
                Object.assign(value, {
                    kind: "python",
                    code: step.python.code,
                    ...(packages.length ? { packages } : {}),
                    ...(own(step.python, "entry") ? { entry: step.python.entry } : {}),
                });
            } else if (step.mcpTool) {
                Object.assign(value, {
                    kind: "mcp_tool",
                    server: step.mcpTool.server,
                    tool: step.mcpTool.tool,
                    ...(own(step.mcpTool, "args") ? { args: step.mcpTool.args } : {}),
                    ...(own(step.mcpTool, "argsFromStepId")
                        ? { argsFrom: step.mcpTool.argsFromStepId }
                        : {}),
                });
            } else if (step.agent) {
                Object.assign(value, {
                    kind: "agent",
                    prompt: step.agent.prompt,
                    ...(own(step.agent, "outputSchema")
                        ? { outputSchema: step.agent.outputSchema }
                        : {}),
                    ...(step.agent.role ? { role: step.agent.role } : {}),
                });
            }
            return value;
        });
        return {
            kind: "kernel",
            command: name,
            title,
            params,
            steps,
            render: kernel.render || {},
            ...(formatVersion === 0
                ? {}
                : {
                    scopeMode: normalizeEnum(
                        kernel.scopeMode,
                        "TENANT_VIEW_SCOPE_MODE_",
                    ),
                }),
            id: destinationId,
            ...(own(kernel, "description") ? { description: kernel.description } : {}),
            ...(kernel.agentic === true ? { agentic: true } : {}),
            ...(own(kernel, "renderSourceStepId")
                ? { renderSource: kernel.renderSourceStepId }
                : {}),
        };
    }
    const canvas = definition.canvas;
    const rows = (canvas.rows || []).map((row) => ({
        cells: (row.cells || []).map((cell) => ({
            kernel_id: cell.kernelId,
            ...(own(cell, "command") ? { command: cell.command } : {}),
            ...(own(cell, "caption") ? { caption: cell.caption } : {}),
            ...(own(cell, "parameters") ? { parameters: cell.parameters } : {}),
            ...(own(cell, "width") ? { width: cell.width } : {}),
        })),
    }));
    return {
        kind: "canvas",
        slug: name,
        title,
        ...(formatVersion === 0
            ? { items: rows[0]?.cells || [] }
            : { rows }),
        id: destinationId,
        ...(own(canvas, "description") ? { description: canvas.description } : {}),
    };
}

function decimalVersion(value) {
    const string = typeof value === "bigint" ? value.toString() : String(value ?? "");
    if (!/^[1-9]\d*$/.test(string)) {
        throw validationError("expectedVersion must be a positive integer");
    }
    return string;
}

function responseVersion(value) {
    if (typeof value === "number" && Number.isSafeInteger(value) && value >= 0) {
        return String(value);
    }
    if (typeof value === "string" && /^\d+$/.test(value)) return value;
    throw responseError("Tenant view version is invalid");
}

function adaptLineage(value) {
    if (!value || typeof value !== "object") return null;
    return {
        source: normalizeEnum(value.source, "TENANT_VIEW_LINEAGE_SOURCE_"),
        id: String(value.id ?? ""),
    };
}

function adaptMetadata(value) {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        throw responseError("Tenant view metadata is missing");
    }
    return {
        id: String(value.id ?? ""),
        kind: normalizeEnum(value.kind, "TENANT_VIEW_KIND_"),
        name: String(value.name ?? ""),
        title: String(value.title ?? ""),
        visibility: normalizeEnum(value.visibility, "TENANT_VIEW_VISIBILITY_"),
        createdBy: pick(value, "created_by", "createdBy") ?? null,
        createdByLabel: String(pick(value, "created_by_label", "createdByLabel") ?? ""),
        objectOrigin: normalizeEnum(
            pick(value, "object_origin", "objectOrigin"),
            "TENANT_VIEW_ORIGIN_",
        ),
        sourceArtifactId: pick(value, "source_artifact_id", "sourceArtifactId") ?? null,
        forkedFrom: adaptLineage(pick(value, "forked_from", "forkedFrom")),
        formatVersion: finiteInteger(
            pick(value, "format_version", "formatVersion"),
            0,
        ),
        scopeMode: normalizeEnum(
            pick(value, "scope_mode", "scopeMode"),
            "TENANT_VIEW_SCOPE_MODE_",
        ),
        stepKinds: Array.isArray(pick(value, "step_kinds", "stepKinds"))
            ? [...pick(value, "step_kinds", "stepKinds")]
            : [],
        renderKind: String(pick(value, "render_kind", "renderKind") ?? ""),
        version: responseVersion(value.version),
        originDeployedAt: pick(value, "origin_deployed_at", "originDeployedAt") ?? null,
        withdrawnAt: pick(value, "withdrawn_at", "withdrawnAt") ?? null,
        hiddenAt: pick(value, "hidden_at", "hiddenAt") ?? null,
        hiddenBy: pick(value, "hidden_by", "hiddenBy") ?? null,
        createdAt: pick(value, "created_at", "createdAt") ?? null,
        updatedAt: pick(value, "updated_at", "updatedAt") ?? null,
    };
}

function adaptTenantView(value) {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        throw responseError("Tenant view response body is missing");
    }
    const definitionJson = pick(value, "definition_json", "definitionJson");
    if (typeof definitionJson !== "string") {
        throw responseError("Tenant view definitionJson is missing");
    }
    let definition;
    try {
        definition = JSON.parse(definitionJson);
    } catch (cause) {
        throw new TenantViewApiError("Tenant view definitionJson is invalid", {
            code: "INVALID_RESPONSE",
            cause,
        });
    }
    return {
        metadata: adaptMetadata(value.metadata),
        definitionJson,
        definition,
    };
}

async function errorForResponse(response, label) {
    const text = typeof response.text === "function"
        ? await response.text().catch(() => "")
        : "";
    let envelope = null;
    try {
        envelope = text ? JSON.parse(text) : null;
    } catch {
        envelope = null;
    }
    const status = response.status ?? 0;
    const code = grpcStatusName(envelope?.code ?? envelope?.error?.code);
    const serverMessage = typeof envelope?.message === "string"
        ? envelope.message
        : typeof envelope?.error?.message === "string"
            ? envelope.error.message
            : "";
    const options = {
        status,
        code,
        serverMessage,
        details: Array.isArray(envelope?.details) ? envelope.details : [],
    };
    const message = `${label} ${status}: ${serverMessage || text || response.statusText || "request failed"}`;
    if (
        status === 409
        || status === 412
        || code === "ABORTED"
        || code === "FAILED_PRECONDITION"
    ) {
        return new TenantViewConflictError(message, options);
    }
    if ([501, 502, 503, 504].includes(status) || code === "UNAVAILABLE" || code === "UNIMPLEMENTED") {
        return new TenantViewUnavailableError(message, options);
    }
    return new TenantViewApiError(message, options);
}

async function request(path, {
    method = "GET",
    body,
    signal,
    label = `${method} ${path}`,
    empty = false,
} = {}) {
    const headers = {};
    const init = { method, signal };
    if (body !== undefined) {
        headers["Content-Type"] = "application/json";
        init.body = JSON.stringify(body);
    }
    if (Object.keys(headers).length) init.headers = headers;

    let response;
    try {
        response = await authedFetch(path, init);
    } catch (cause) {
        if (cause?.name === "AbortError") throw cause;
        throw new TenantViewUnavailableError(`${label} is unavailable`, {
            code: cause?.name === "TimeoutError" ? "TIMEOUT" : "NETWORK_ERROR",
            cause,
        });
    }
    if (!response.ok) throw await errorForResponse(response, label);
    if (empty) return undefined;
    try {
        return await response.json();
    } catch (cause) {
        throw new TenantViewApiError(`${label} returned invalid JSON`, {
            status: response.status,
            code: "INVALID_RESPONSE",
            cause,
        });
    }
}

let capabilitiesCache = null;
let capabilitiesInflight = null;
onAuthIdentityChange(() => {
    capabilitiesCache = null;
    capabilitiesInflight = null;
});

export async function getTenantViewCapabilities({ force = false, signal } = {}) {
    if (!force && capabilitiesCache) return capabilitiesCache;
    if (!force && !signal && capabilitiesInflight) return capabilitiesInflight;

    const epoch = getAuthIdentityEpoch();
    const load = request("/v1/me", {
        signal,
        label: "GetCurrentUser",
    }).then((response) => {
        if (getAuthIdentityEpoch() !== epoch) {
            const error = new Error("Authentication context changed while loading tenant view capabilities");
            error.name = "AuthContextChangedError";
            throw error;
        }
        const raw = pick(response, "tenant_view_capabilities", "tenantViewCapabilities");
        if (!raw) {
            throw new TenantViewUnavailableError(
                "The server does not advertise tenant view capabilities",
                { code: "CAPABILITIES_UNAVAILABLE" },
            );
        }
        const capabilities = normalizeTenantViewCapabilities(raw);
        capabilitiesCache = capabilities;
        return capabilities;
    });

    if (signal) return load;
    const requestPromise = load.finally(() => {
        if (capabilitiesInflight === requestPromise) capabilitiesInflight = null;
    });
    capabilitiesInflight = requestPromise;
    return requestPromise;
}

function jsonDepth(value) {
    if (value === null || typeof value !== "object") return 0;
    let maximum = 0;
    const stack = [{ value, depth: 1 }];
    while (stack.length) {
        const current = stack.pop();
        maximum = Math.max(maximum, current.depth);
        const children = Array.isArray(current.value)
            ? current.value
            : Object.values(current.value);
        for (const child of children) {
            if (child !== null && typeof child === "object") {
                stack.push({ value: child, depth: current.depth + 1 });
            }
        }
    }
    return maximum;
}

function definitionSteps(value, kind) {
    const stack = [{ value, path: "$" }];
    while (stack.length) {
        const current = stack.pop();
        if (Array.isArray(current.value)) {
            current.value.forEach((child, index) => {
                stack.push({ value: child, path: `${current.path}[${index}]` });
            });
            continue;
        }
        if (!current.value || typeof current.value !== "object") continue;
        for (const [key, child] of Object.entries(current.value)) {
            const path = `${current.path}.${key}`;
            if (key === "steps") {
                if (kind === "canvas") {
                    throw validationError(`${path} is not allowed in a canvas definition`);
                }
                if (current.path !== "$") {
                    throw validationError(`${path} is not allowed; kernel steps must be at $.steps`);
                }
            }
            stack.push({ value: child, path });
        }
    }

    if (kind === "canvas") return [];
    if (!Array.isArray(value.steps) || value.steps.length === 0) {
        throw validationError("kernel definition must contain a non-empty steps array");
    }
    return value.steps.map((step, index) => {
        if (!step || typeof step !== "object" || Array.isArray(step)) {
            throw validationError(`$.steps[${index}] must be an object`);
        }
        return { step, path: `$.steps[${index}]` };
    });
}

function definitionString(definitionJson) {
    if (typeof definitionJson === "string") return definitionJson;
    try {
        return JSON.stringify(definitionJson);
    } catch (cause) {
        throw new TenantViewApiError("definitionJson must be JSON-serializable", {
            code: "CLIENT_VALIDATION",
            cause,
        });
    }
}

function rejectUnsupportedKeys(value, path, allowed) {
    for (const key of Object.keys(value)) {
        if (!allowed.has(key)) {
            throw validationError(`${path}.${key} is not supported by the typed definition schema`);
        }
    }
}

export function validateTenantViewDefinition(definitionJson, {
    kind,
    formatVersion,
    visibility = "draft",
    forPublish = false,
    capabilities = DEFAULT_TENANT_VIEW_CAPABILITIES,
} = {}) {
    const normalizedCapabilities = normalizeTenantViewCapabilities(capabilities);
    const normalizedKind = normalizeEnum(
        wireEnum(kind, KIND_TO_WIRE, "TENANT_VIEW_KIND_", "kind"),
        "TENANT_VIEW_KIND_",
    );
    const normalizedVisibility = normalizeEnum(
        wireEnum(
            visibility,
            VISIBILITY_TO_WIRE,
            "TENANT_VIEW_VISIBILITY_",
            "visibility",
        ),
        "TENANT_VIEW_VISIBILITY_",
    );
    const version = finiteInteger(formatVersion, -1);
    if (
        version < normalizedCapabilities.minWritableFormatVersion
        || version > normalizedCapabilities.currentFormatVersion
    ) {
        throw validationError(
            `formatVersion ${formatVersion} is outside the server's writable range `
            + `${normalizedCapabilities.minWritableFormatVersion}..${normalizedCapabilities.currentFormatVersion}`,
        );
    }

    const raw = definitionString(definitionJson);
    const bytes = TEXT_ENCODER.encode(raw).byteLength;
    if (bytes > normalizedCapabilities.maxDefinitionBytes) {
        throw validationError(
            `definitionJson is ${bytes} bytes; maximum is ${normalizedCapabilities.maxDefinitionBytes}`,
        );
    }

    let definition;
    try {
        definition = JSON.parse(raw);
    } catch (cause) {
        throw new TenantViewApiError("definitionJson must be valid JSON", {
            code: "CLIENT_VALIDATION",
            cause,
        });
    }
    if (!definition || typeof definition !== "object" || Array.isArray(definition)) {
        throw validationError("definitionJson must contain a JSON object");
    }
    const depth = jsonDepth(definition);
    if (depth > normalizedCapabilities.maxDefinitionDepth) {
        throw validationError(
            `definitionJson depth is ${depth}; maximum is ${normalizedCapabilities.maxDefinitionDepth}`,
        );
    }

    const steps = definitionSteps(definition, normalizedKind);
    if (normalizedKind === "kernel") {
        if (steps.length > normalizedCapabilities.maxKernelSteps) {
            throw validationError(
                `kernel definition contains ${steps.length} steps; maximum is `
                + normalizedCapabilities.maxKernelSteps,
            );
        }
        if (!definition.render
            || typeof definition.render !== "object"
            || Array.isArray(definition.render)
            || typeof definition.render.kind !== "string"
            || definition.render.kind.length === 0) {
            throw validationError("kernel definition must contain a non-empty render.kind");
        }
        const scopeMode = definition.scopeMode;
        if (!(
            (version === 0 && scopeMode === undefined)
            || scopeMode === "global"
            || scopeMode === "scoped"
        )) {
            throw validationError(
                `formatVersion ${version} kernel definition must set scopeMode to "global" or "scoped"`,
            );
        }
    } else if (Object.prototype.hasOwnProperty.call(definition, "scopeMode")) {
        throw validationError("canvas definition must not declare scopeMode");
    }

    const stepKinds = [...new Set(steps.map(({ step, path }) => {
        if (typeof step.kind !== "string" || step.kind.length === 0) {
            throw validationError(`${path}.kind must be a non-empty string`);
        }
        return step.kind;
    }))].sort();
    const allowedKinds = new Set(
        forPublish || normalizedVisibility === "published"
            ? normalizedCapabilities.publishableStepKinds
            : normalizedCapabilities.knownStepKinds,
    );
    const unsupported = stepKinds.find((stepKind) => !allowedKinds.has(stepKind));
    if (unsupported) {
        throw validationError(
            `${unsupported} is not supported for ${forPublish || normalizedVisibility === "published" ? "published" : "writable"} tenant views`,
        );
    }

    rejectUnsupportedKeys(
        definition,
        "$",
        normalizedKind === "kernel"
            ? KERNEL_DEFINITION_KEYS
            : new Set(
                [...CANVAS_DEFINITION_KEYS].filter(
                    (key) => key !== (version === 0 ? "rows" : "items"),
                ),
            ),
    );
    if (normalizedKind === "kernel") {
        if (own(definition, "params")) {
            if (!definition.params
                || typeof definition.params !== "object"
                || Array.isArray(definition.params)) {
                throw validationError("$.params must be an object");
            }
            for (const [name, parameter] of Object.entries(definition.params)) {
                if (!parameter || typeof parameter !== "object" || Array.isArray(parameter)) {
                    throw validationError(`$.params.${name} must be an object`);
                }
                rejectUnsupportedKeys(parameter, `$.params.${name}`, PARAMETER_KEYS);
            }
        }
        for (const { step, path } of steps) {
            const stepKeys = STEP_KEYS[step.kind];
            if (!stepKeys) {
                throw validationError(
                    `${step.kind} is not supported by this typed client schema`,
                );
            }
            rejectUnsupportedKeys(step, path, stepKeys);
        }
    }

    if (normalizedKind === "canvas") {
        const cells = [];
        if (version === 0) {
            if (!Array.isArray(definition.items)) {
                throw validationError("formatVersion 0 canvas definition must contain an items array");
            }
            cells.push(...definition.items);
        } else {
            if (!Array.isArray(definition.rows)) {
                throw validationError("canvas definition must contain a rows array");
            }
            if (definition.rows.length > normalizedCapabilities.maxCanvasRows) {
                throw validationError(
                    `canvas definition contains ${definition.rows.length} rows; maximum is `
                    + normalizedCapabilities.maxCanvasRows,
                );
            }
            definition.rows.forEach((row, index) => {
                if (!row || typeof row !== "object" || !Array.isArray(row.cells)) {
                    throw validationError(`$.rows[${index}].cells must be an array`);
                }
                rejectUnsupportedKeys(row, `$.rows[${index}]`, CANVAS_ROW_KEYS);
                cells.push(...row.cells);
            });
        }
        cells.forEach((cell, index) => {
            if (!cell || typeof cell !== "object" || Array.isArray(cell)) {
                throw validationError(`canvas cell ${index} must be an object`);
            }
            rejectUnsupportedKeys(cell, `canvas cell ${index}`, CANVAS_CELL_KEYS);
            if (own(cell, "width")
                && (typeof cell.width !== "number"
                    || !Number.isFinite(cell.width)
                    || cell.width <= 0)) {
                throw validationError(
                    `canvas cell ${index}.width must be a finite positive number`,
                );
            }
        });
        if (cells.length > normalizedCapabilities.maxCanvasCells) {
            throw validationError(
                `canvas definition contains ${cells.length} cells; maximum is `
                + normalizedCapabilities.maxCanvasCells,
            );
        }
    }

    return {
        definition,
        definitionJson: raw,
        formatVersion: version,
        stepKinds,
        bytes,
        depth,
    };
}

function queryPath(path, entries) {
    const params = new URLSearchParams();
    for (const [key, value] of entries) {
        if (value !== undefined && value !== null && value !== "") {
            params.set(key, String(value));
        }
    }
    const query = params.toString();
    return query ? `${path}?${query}` : path;
}

function pageSize(value) {
    if (value === undefined || value === null) return undefined;
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed < 1 || parsed > MAX_PAGE_SIZE) {
        throw validationError(`pageSize must be between 1 and ${MAX_PAGE_SIZE}`);
    }
    return parsed;
}

export async function listTenantViews({
    kind,
    visibility,
    includeHidden = false,
    pageSize: requestedPageSize,
    pageToken,
    signal,
} = {}) {
    const path = queryPath("/v1/tenant-views", [
        ["kind", wireEnum(kind, KIND_TO_WIRE, "TENANT_VIEW_KIND_", "kind", { allowUnspecified: true })],
        ["visibility", wireEnum(
            visibility,
            VISIBILITY_TO_WIRE,
            "TENANT_VIEW_VISIBILITY_",
            "visibility",
            { allowUnspecified: true },
        )],
        ["includeHidden", includeHidden ? true : undefined],
        ["pageSize", pageSize(requestedPageSize)],
        ["pageToken", pageToken],
    ]);
    const response = await request(path, { signal, label: "ListTenantViews" });
    const values = pick(response, "tenant_views", "tenantViews");
    if (values !== undefined && !Array.isArray(values)) {
        throw responseError("ListTenantViews tenantViews must be an array");
    }
    return {
        tenantViews: (values ?? []).map(adaptMetadata),
        nextPageToken: String(pick(response, "next_page_token", "nextPageToken") ?? ""),
    };
}

export async function getTenantView(tenantViewId, { signal } = {}) {
    const response = await request("/v1/tenant-views:get", {
        method: "POST",
        body: { tenantViewId: requireId(tenantViewId) },
        signal,
        label: "GetTenantView",
    });
    return adaptTenantView(pick(response, "tenant_view", "tenantView"));
}

export async function inspectTenantViewSecretFindings(tenantViewId, { signal } = {}) {
    const response = await request("/v1/tenant-views:inspect-secret-findings", {
        method: "POST",
        body: { tenantViewId: requireId(tenantViewId) },
        signal,
        label: "InspectTenantViewSecretFindings",
    });
    const findings = pick(response, "findings", "findings");
    if (!Array.isArray(findings)) {
        throw responseError("secret finding inspection must return a findings array");
    }
    const contentDigest = canonicalBase64(
        pick(response, "content_digest", "contentDigest"),
        "secret finding contentDigest",
        32,
    );
    return {
        tenantViewId: requireId(
            pick(response, "tenant_view_id", "tenantViewId"),
        ),
        version: responseVersion(response?.version),
        contentDigest,
        detectorVersion: boundedUtf8String(
            pick(response, "detector_version", "detectorVersion"),
            "secret detector version",
            64,
            { nonblank: true },
        ),
        findings: findings.map((finding) => ({
            kind: boundedUtf8String(finding?.kind, "secret finding kind", 64, {
                nonblank: true,
            }),
            jsonPointer: boundedUtf8String(
                pick(finding, "json_pointer", "jsonPointer"),
                "secret finding JSON pointer",
                1024,
            ),
        })),
        totalFindingCount: finiteInteger(
            pick(response, "total_finding_count", "totalFindingCount"),
            findings.length,
        ),
        findingsTruncated:
            pick(response, "findings_truncated", "findingsTruncated") === true,
    };
}

async function capabilitiesForWrite(provided, signal) {
    return provided
        ? normalizeTenantViewCapabilities(provided)
        : getTenantViewCapabilities({ signal });
}

function createBody(input, capabilities, visibility = "draft") {
    const formatVersion = input.formatVersion ?? capabilities.currentFormatVersion;
    const validated = validateTenantViewDefinition(
        input.definitionJson ?? input.definition,
        {
            kind: input.kind,
            formatVersion,
            visibility,
            capabilities,
        },
    );
    return {
        kind: wireEnum(input.kind, KIND_TO_WIRE, "TENANT_VIEW_KIND_", "kind"),
        name: boundedUtf8String(input.name, "name", 64, { nonblank: true }),
        title: boundedUtf8String(input.title, "title", 255, { nonblank: true }),
        formatVersion,
        definitionJson: validated.definitionJson,
        createRequestId: createRequestId(input.createRequestId),
    };
}

export async function createTenantView(input, { capabilities: provided, signal } = {}) {
    const capabilities = await capabilitiesForWrite(provided, signal);
    const response = await request("/v1/tenant-views", {
        method: "POST",
        body: createBody(input ?? {}, capabilities),
        signal,
        label: "CreateTenantView",
    });
    return adaptTenantView(pick(response, "tenant_view", "tenantView"));
}

export async function importTenantView(input, { capabilities: provided, signal } = {}) {
    const capabilities = await capabilitiesForWrite(provided, signal);
    const body = createBody(input ?? {}, capabilities);
    body.secretOverrides = (input?.secretOverrides || []).map((override) =>
        secretFindingOverrideBody(override));
    const response = await request("/v1/tenant-views:import", {
        method: "POST",
        body,
        signal,
        label: "ImportTenantView",
    });
    return adaptTenantView(pick(response, "tenant_view", "tenantView"));
}

export async function importTenantViewBundle(input, { capabilities: provided, signal } = {}) {
    const capabilities = await capabilitiesForWrite(provided, signal);
    const objects = Array.isArray(input?.objects) ? input.objects : [];
    if (objects.length < 1 || objects.length > 21) {
        throw validationError("bundle import must contain 1 through 21 objects");
    }
    const preparedObjects = objects.map((object) => {
        const formatVersion =
            object.formatVersion ?? capabilities.currentFormatVersion;
        const validated = validateTenantViewDefinition(
            object.definitionJson ?? object.definition,
            {
                kind: object.kind,
                formatVersion,
                visibility: "draft",
                capabilities,
            },
        );
        const definition = typedDefinition(
            validated.definition,
            object.kind,
            formatVersion,
        );
        const destinationId = createRequestId(object.destinationId);
        const name = boundedUtf8String(object.name, "name", 64, { nonblank: true });
        const title = boundedUtf8String(object.title, "title", 255, { nonblank: true });
        return {
            sourceId: object.sourceId,
            destinationId,
            canonicalDefinition: canonicalDefinitionFromTyped(
                definition,
                object.kind,
                name,
                title,
                destinationId,
                formatVersion,
            ),
            body: {
                destinationId,
                kind: wireEnum(
                    object.kind,
                    KIND_TO_WIRE,
                    "TENANT_VIEW_KIND_",
                    "kind",
                ),
                name,
                title,
                formatVersion,
                definition,
            },
        };
    });
    const requestedOverrides = Array.isArray(input?.secretOverrides)
        ? input.secretOverrides
        : [];
    if (requestedOverrides.length > capabilities.maxSecretFindingOverrides) {
        throw validationError(
            `secretOverrides must contain at most ${capabilities.maxSecretFindingOverrides} entries`,
        );
    }
    if (requestedOverrides.length && !capabilities.secretDetectorVersion) {
        throw validationError(
            "The server has not advertised secret-finding overrides; update the backend first",
        );
    }
    const secretOverrides = [];
    for (const requested of requestedOverrides) {
        const sourceId = requested?.objectId ?? requested?.object_id;
        const prepared = preparedObjects.find((item) => item.sourceId === sourceId);
        if (!prepared) {
            throw validationError("secret override objectId must name an imported object");
        }
        const contentDigest = await tenantViewSecretOverrideDigest(
            prepared.canonicalDefinition,
            input?.cryptoImpl,
        );
        secretOverrides.push(secretFindingOverrideBody(requested, {
            objectId: prepared.destinationId,
            contentDigest,
            detectorVersion: capabilities.secretDetectorVersion,
        }));
    }
    const body = {
        importRequestId: createRequestId(input.importRequestId),
        rootDestinationId: createRequestId(input.rootDestinationId),
        objects: preparedObjects.map((object) => object.body),
        secretOverrides,
    };
    const response = await request("/v1/tenant-views:import-bundle", {
        method: "POST",
        body,
        signal,
        label: "ImportTenantViewBundle",
    });
    const values = pick(response, "tenant_views", "tenantViews");
    if (!Array.isArray(values)) {
        throw responseError("bundle import response tenantViews must be an array");
    }
    return {
        rootTenantViewId: String(
            pick(response, "root_tenant_view_id", "rootTenantViewId") ?? "",
        ),
        tenantViews: values.map(adaptTenantView),
        replayed: response?.replayed === true,
    };
}

export async function updateTenantView(input, { capabilities: provided, signal } = {}) {
    const requestInput = input ?? {};
    const expectedVersion = decimalVersion(requestInput.expectedVersion);
    const body = {
        tenantViewId: requireId(requestInput.tenantViewId),
        expectedVersion,
    };
    for (const key of ["name", "title"]) {
        if (own(requestInput, key)) {
            body[key] = boundedUtf8String(
                requestInput[key],
                key,
                key === "name" ? 64 : 255,
                { nonblank: true },
            );
        }
    }
    if (own(requestInput, "formatVersion")) body.formatVersion = requestInput.formatVersion;

    if (own(requestInput, "definitionJson") || own(requestInput, "definition")) {
        if (requestInput.kind === undefined || requestInput.visibility === undefined) {
            throw validationError(
                "kind and visibility are required when updating definitionJson",
            );
        }
        if (requestInput.formatVersion === undefined) {
            throw validationError(
                "formatVersion is required when updating definitionJson",
            );
        }
        const capabilities = await capabilitiesForWrite(provided, signal);
        const validated = validateTenantViewDefinition(
            requestInput.definitionJson ?? requestInput.definition,
            {
                kind: requestInput.kind,
                formatVersion: requestInput.formatVersion,
                visibility: requestInput.visibility,
                capabilities,
            },
        );
        body.definitionJson = validated.definitionJson;
    } else if (own(requestInput, "formatVersion")) {
        const capabilities = await capabilitiesForWrite(provided, signal);
        const version = finiteInteger(requestInput.formatVersion, -1);
        if (
            version < capabilities.minWritableFormatVersion
            || version > capabilities.currentFormatVersion
        ) {
            throw validationError("formatVersion is outside the server's writable range");
        }
    }

    const response = await request("/v1/tenant-views:update", {
        method: "POST",
        body,
        signal,
        label: "UpdateTenantView",
    });
    return adaptTenantView(pick(response, "tenant_view", "tenantView"));
}

export async function forkTenantView(input, { signal } = {}) {
    const requestInput = input ?? {};
    const hasStored = Boolean(requestInput.stored);
    const hasBundled = Boolean(requestInput.bundled);
    if (hasStored === hasBundled) {
        throw validationError("fork requires exactly one stored or bundled source");
    }

    let expectedVersion;
    const body = {
        createRequestId: createRequestId(requestInput.createRequestId),
    };
    if (hasStored) {
        expectedVersion = decimalVersion(requestInput.stored.expectedVersion);
        body.stored = {
            tenantViewId: requireId(requestInput.stored.tenantViewId),
            expectedVersion,
        };
    } else {
        const bundled = requestInput.bundled;
        body.bundled = {
            sourceId: requireId(bundled.sourceId, "sourceId"),
        };
    }

    const response = await request("/v1/tenant-views:fork", {
        method: "POST",
        body,
        signal,
        label: "ForkTenantView",
    });
    return adaptTenantView(pick(response, "tenant_view", "tenantView"));
}

async function versionedMutation(path, label, tenantViewId, expectedVersion, signal) {
    const version = decimalVersion(expectedVersion);
    const response = await request(path, {
        method: "POST",
        body: {
            tenantViewId: requireId(tenantViewId),
            expectedVersion: version,
        },
        signal,
        label,
    });
    const tenantView = adaptTenantView(pick(response, "tenant_view", "tenantView"));
    const rawDependents = pick(response, "visible_dependents", "visibleDependents");
    if (rawDependents === undefined) return tenantView;
    if (!Array.isArray(rawDependents)) {
        throw responseError(`${label} visibleDependents must be an array`);
    }
    return {
        ...tenantView,
        visibleDependents: rawDependents.map(adaptMetadata),
    };
}

export async function publishTenantView(
    tenantViewId,
    expectedVersion,
    { signal, secretOverrides = [] } = {},
) {
    const response = await request("/v1/tenant-views:publish", {
        method: "POST",
        body: {
            tenantViewId: requireId(tenantViewId),
            expectedVersion: decimalVersion(expectedVersion),
            secretOverrides: secretOverrides.map((override) =>
                secretFindingOverrideBody(override)),
        },
        signal,
        label: "PublishTenantView",
    });
    return adaptTenantView(pick(response, "tenant_view", "tenantView"));
}

export function unpublishTenantView(tenantViewId, expectedVersion, { signal } = {}) {
    return versionedMutation(
        "/v1/tenant-views:unpublish",
        "UnpublishTenantView",
        tenantViewId,
        expectedVersion,
        signal,
    );
}

export function hideTenantView(tenantViewId, expectedVersion, { signal } = {}) {
    return versionedMutation(
        "/v1/tenant-views:hide",
        "HideTenantView",
        tenantViewId,
        expectedVersion,
        signal,
    );
}

export function unhideTenantView(tenantViewId, expectedVersion, { signal } = {}) {
    return versionedMutation(
        "/v1/tenant-views:unhide",
        "UnhideTenantView",
        tenantViewId,
        expectedVersion,
        signal,
    );
}

export async function deleteTenantView(
    tenantViewId,
    expectedVersion,
    { force = false, signal } = {},
) {
    const version = decimalVersion(expectedVersion);
    const body = {
        tenantViewId: requireId(tenantViewId),
        expectedVersion: version,
    };
    if (force) body.force = true;
    return request("/v1/tenant-views:delete", {
        method: "POST",
        body,
        signal,
        label: "DeleteTenantView",
        empty: true,
    });
}

export async function listTenantViewDependents(
    tenantViewId,
    { pageSize: requestedPageSize, pageToken, signal } = {},
) {
    const response = await request("/v1/tenant-views:list-dependents", {
        method: "POST",
        body: {
            tenantViewId: requireId(tenantViewId),
            pageSize: pageSize(requestedPageSize),
            pageToken: pageToken || undefined,
        },
        signal,
        label: "ListTenantViewDependents",
    });
    const values = response?.dependents;
    if (values !== undefined && !Array.isArray(values)) {
        throw responseError("ListTenantViewDependents dependents must be an array");
    }
    return {
        dependents: (values ?? []).map(adaptMetadata),
        nextPageToken: String(pick(response, "next_page_token", "nextPageToken") ?? ""),
    };
}

export async function recordTenantViewRun(
    tenantViewId,
    { outcome, reasonCategory = null, analyticsQueryCount = 0 } = {},
    { signal } = {},
) {
    const outcomeKey = String(outcome ?? "").toLowerCase();
    const normalizedOutcome = typeof outcome === "string" && outcome.startsWith("TENANT_VIEW_RUN_OUTCOME_")
        ? outcome
        : RUN_OUTCOME_TO_WIRE[outcomeKey];
    if (!normalizedOutcome) throw validationError("outcome is not recognized");
    const defaultReasonCategory = DEFAULT_RUN_REASON_CATEGORY[outcomeKey]
        || DEFAULT_RUN_REASON_CATEGORY[
            Object.entries(RUN_OUTCOME_TO_WIRE)
                .find(([, wire]) => wire === normalizedOutcome)?.[0]
        ];
    const normalizedReasonCategory = typeof reasonCategory === "string"
        && reasonCategory.startsWith("TENANT_VIEW_RUN_REASON_CATEGORY_")
        ? reasonCategory
        : RUN_REASON_CATEGORY_TO_WIRE[
            String(reasonCategory || defaultReasonCategory || "").toLowerCase()
        ];
    if (!normalizedReasonCategory) {
        throw validationError("reasonCategory is not recognized");
    }
    if (
        !Number.isSafeInteger(analyticsQueryCount)
        || analyticsQueryCount < 0
        || analyticsQueryCount > 400
    ) {
        throw validationError("analyticsQueryCount must be an integer between 0 and 400");
    }
    return request("/v1/tenant-views:record-run", {
        method: "POST",
        body: {
            viewId: requireId(tenantViewId),
            outcome: normalizedOutcome,
            reasonCategory: normalizedReasonCategory,
            analyticsQueryCount,
        },
        signal,
        label: "RecordTenantViewRun",
        empty: true,
    });
}

export function recordTenantViewOpen(tenantViewId, { signal } = {}) {
    return request("/v1/tenant-views:record-open", {
        method: "POST",
        body: { viewId: requireId(tenantViewId) },
        signal,
        label: "RecordTenantViewOpen",
        empty: true,
    });
}

export function _resetTenantViewsApiForTests() {
    capabilitiesCache = null;
    capabilitiesInflight = null;
}
